import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, Paperclip, X } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import type { SupportAttachment } from '../../../common/models/support.model';
import { ACCEPT_ATTR, MAX_ATTACHMENTS, attachmentProblem, formatBytes } from '../support-meta';
import { AttachmentThumb } from './AttachmentView';

interface Pending {
  id: string;
  file: File;
  preview?: string;
  progress: number;
  uploaded?: SupportAttachment;
  error?: string;
}

export interface ComposerProps {
  upload: (file: File, onProgress: (fraction: number) => void) => Promise<SupportAttachment>;
  onSend: (message: { body: string; attachments: SupportAttachment[] }) => Promise<void>;
  placeholder?: string;
  autoFocus?: boolean;
  /** Controls beside the send button (e.g. staff's "internal note" switch). */
  extra?: React.ReactNode;
  /** `note`: styled as a staff-only internal note. */
  tone?: 'default' | 'note';
  className?: string;
  disabled?: boolean;
  /** Called on every keystroke with the draft text (e.g. to persist it). */
  onDraftChange?: (text: string) => void;
  initialText?: string;
}

/**
 * Text plus attachments. Files upload as soon as they're added (button, drop
 * or paste) so sending is instant; Enter sends, Shift+Enter makes a new line.
 */
export function Composer({
  upload,
  onSend,
  placeholder,
  autoFocus,
  extra,
  tone = 'default',
  className,
  disabled,
  onDraftChange,
  initialText = '',
}: ComposerProps) {
  const { t } = useTranslation();
  const [text, setText] = useState(initialText);
  const [pending, setPending] = useState<Pending[]>([]);
  const [problem, setProblem] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Grow with the text up to ~8 lines.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 180)}px`;
  }, [text]);

  // Free object URLs when previews go away.
  useEffect(() => () => pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const addFiles = useCallback(
    (files: File[]) => {
      setProblem(null);
      const room = MAX_ATTACHMENTS - pending.length;
      if (files.length > room) setProblem(t('SUPPORT.COMPOSER.TOO_MANY', { max: MAX_ATTACHMENTS }));
      for (const file of files.slice(0, Math.max(0, room))) {
        const issue = attachmentProblem(file);
        if (issue) {
          setProblem(t(issue.key, issue.opts));
          continue;
        }
        const item: Pending = {
          id: uuidv4(),
          file,
          progress: 0,
          preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined,
        };
        setPending((list) => [...list, item]);
        upload(file, (fraction) => setPending((list) => list.map((p) => (p.id === item.id ? { ...p, progress: fraction } : p))))
          .then((uploaded) => setPending((list) => list.map((p) => (p.id === item.id ? { ...p, uploaded, progress: 1 } : p))))
          .catch((e) => setPending((list) => list.map((p) => (p.id === item.id ? { ...p, error: e?.message || t('SUPPORT.COMPOSER.UPLOAD_FAILED') } : p))));
      }
    },
    [pending.length, upload, t],
  );

  const remove = (id: string) =>
    setPending((list) => {
      const gone = list.find((p) => p.id === id);
      if (gone?.preview) URL.revokeObjectURL(gone.preview);
      return list.filter((p) => p.id !== id);
    });

  const uploading = pending.some((p) => !p.uploaded && !p.error);
  const ready = pending.filter((p) => p.uploaded).map((p) => p.uploaded!);
  const canSend = !disabled && !sending && !uploading && (text.trim().length > 0 || ready.length > 0);

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await onSend({ body: text.trim(), attachments: ready });
      setText('');
      onDraftChange?.('');
      pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
      setPending([]);
      setProblem(null);
    } catch (e: any) {
      // Keep the draft: nothing is lost when a send fails.
      setProblem(e?.message || t('SUPPORT.COMPOSER.SEND_FAILED'));
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div
      className={cn(
        'relative flex flex-col gap-2 rounded-2xl border bg-card p-2 transition-colors focus-within:border-foreground/30',
        tone === 'note' && 'border-dashed border-warning/60 bg-warning/5',
        dragging && 'border-foreground/40 bg-muted/60',
        className,
      )}
      onDragOver={(e) => {
        if (!e.dataTransfer?.types?.includes('Files')) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragging(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.files?.length) return;
        e.preventDefault();
        setDragging(false);
        addFiles(Array.from(e.dataTransfer.files));
      }}
      data-testid="support-composer"
    >
      {dragging && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-2xl text-sm font-medium text-muted-foreground">
          {t('SUPPORT.COMPOSER.DROP_HERE')}
        </div>
      )}

      {pending.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1 pt-1">
          {pending.map((p) => (
            <div
              key={p.id}
              className={cn('group relative flex max-w-52 items-center gap-2 rounded-xl border bg-background py-1.5 pe-7 ps-1.5 text-xs', p.error && 'border-destructive/40')}
              data-testid="pending-attachment"
            >
              <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-muted text-muted-foreground">
                <AttachmentThumb file={p.file} url={p.preview} />
              </span>
              <span className="min-w-0">
                <span className="block truncate font-medium">{p.file.name}</span>
                <span className={cn('block truncate', p.error ? 'text-destructive' : 'text-muted-foreground')}>
                  {p.error || (p.uploaded ? formatBytes(p.file.size) : `${Math.round(p.progress * 100)}%`)}
                </span>
              </span>
              {!p.uploaded && !p.error && (
                <span className="absolute inset-x-1.5 bottom-0.5 h-0.5 overflow-hidden rounded-full bg-muted">
                  <span className="block h-full bg-foreground transition-all" style={{ width: `${Math.max(4, p.progress * 100)}%` }} />
                </span>
              )}
              <button
                type="button"
                onClick={() => remove(p.id)}
                className="absolute end-1 top-1 rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                aria-label={t('SUPPORT.COMPOSER.REMOVE', { name: p.file.name })}
              >
                <X className="size-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <textarea
        ref={inputRef}
        value={text}
        rows={1}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder || t('SUPPORT.COMPOSER.PLACEHOLDER')}
        onChange={(e) => {
          setText(e.target.value);
          onDraftChange?.(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault();
            send();
          }
        }}
        onPaste={(e) => {
          const files = Array.from(e.clipboardData?.files || []);
          if (files.length) {
            e.preventDefault();
            addFiles(files);
          }
        }}
        className="max-h-44 min-h-9 w-full resize-none bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-muted-foreground"
        aria-label={placeholder || t('SUPPORT.COMPOSER.PLACEHOLDER')}
      />

      {problem && <p className="px-2 text-xs text-destructive" role="alert">{problem}</p>}

      <div className="flex items-center gap-1">
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={ACCEPT_ATTR}
          className="hidden"
          data-testid="support-file-input"
          onChange={(e) => {
            addFiles(Array.from(e.target.files || []));
            e.target.value = '';
          }}
        />
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => fileRef.current?.click()}
          disabled={disabled || pending.length >= MAX_ATTACHMENTS}
          aria-label={t('SUPPORT.COMPOSER.ATTACH')}
          title={t('SUPPORT.COMPOSER.ATTACH_HINT')}
        >
          <Paperclip />
        </Button>
        <div className="flex min-w-0 flex-1 items-center gap-2">{extra}</div>
        <Button
          type="button"
          size="icon-sm"
          className="rounded-full"
          onClick={send}
          disabled={!canSend}
          aria-label={t('SUPPORT.COMPOSER.SEND')}
        >
          {sending || uploading ? <Spinner /> : <ArrowUp />}
        </Button>
      </div>
    </div>
  );
}
